import { useState, useEffect } from "react";
import { C } from "../lib/colors";

// Module-level pub/sub for toasts. Anyone can call toast.success / .error
// from anywhere in the tree without threading a context through props.
// The single <Toasts/> portal subscribes on mount and renders the queue.

let nextId = 1;
let listeners = [];
let queue = [];

const notify = () => listeners.forEach((fn) => fn(queue));

const remove = (id) => {
  queue = queue.filter((t) => t.id !== id);
  notify();
};

const push = (type, message, ttlMs = 4000) => {
  const id = nextId++;
  queue = [...queue, { id, type, message }];
  notify();
  setTimeout(() => remove(id), ttlMs);
  return id;
};

export const toast = {
  success: (message, ttlMs) => push("success", message, ttlMs),
  error:   (message, ttlMs) => push("error",   message, ttlMs ?? 6000),
  info:    (message, ttlMs) => push("info",    message, ttlMs),
  dismiss: (id)             => remove(id),
};

const TONE = {
  success: { color: C.green,  border: `${C.green}40`,  background: `${C.green}10`,  icon: "✓" },
  error:   { color: C.red,    border: "rgba(255,82,82,0.4)",  background: "rgba(255,82,82,0.1)", icon: "⚠" },
  info:    { color: C.blue,   border: `${C.blue}40`,   background: `${C.blue}10`,   icon: "ℹ" },
};

export const Toasts = () => {
  const [items, setItems] = useState(queue);

  useEffect(() => {
    listeners.push(setItems);
    return () => { listeners = listeners.filter((fn) => fn !== setItems); };
  }, []);

  if (!items.length) return null;

  return (
    <div style={{
      position: "fixed", bottom: 24, left: 24, zIndex: 300,
      display: "flex", flexDirection: "column", gap: 8,
      maxWidth: "min(400px, calc(100vw - 48px))",
      pointerEvents: "none",   // toasts shouldn't block clicks underneath
    }}
      aria-live="polite" aria-atomic="false">
      {items.map((t) => {
        const tone = TONE[t.type] || TONE.info;
        return (
          <div key={t.id}
            role={t.type === "error" ? "alert" : "status"}
            style={{
              pointerEvents: "auto",
              background: C.s1,
              border: `1px solid ${tone.border}`,
              borderLeft: `3px solid ${tone.color}`,
              borderRadius: 9,
              padding: "10px 14px",
              fontSize: 13, color: C.text, lineHeight: 1.5,
              boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
              display: "flex", alignItems: "flex-start", gap: 10,
              fontFamily: "'Instrument Sans',sans-serif",
              animation: "fadeUp 0.18s ease",
            }}>
            <span style={{ color: tone.color, fontSize: 14, flexShrink: 0, marginTop: 1 }}>{tone.icon}</span>
            <div style={{ flex: 1, minWidth: 0, whiteSpace: "pre-wrap" }}>{t.message}</div>
            <button onClick={() => remove(t.id)} aria-label="Dismiss"
              style={{
                background: "transparent", border: "none", color: C.muted,
                fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1,
                fontFamily: "'DM Mono',monospace", flexShrink: 0,
              }}>
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
};
